/**
 * 치과 칸 검사 — 서버(core clinic/verify ClinicFieldRules)와 같은 규칙.
 * 사업자등록번호는 숫자 10자리 + 국세청 체크섬(가중치 1,3,7,1,3,7,1,3,5), 홈페이지는 http(s) 주소(스킴 없으면 https).
 */
const WEIGHTS: number[] = [1, 3, 7, 1, 3, 7, 1, 3, 5];

/** 숫자만 */
export const businessNumberDigits = (raw: string | null | undefined): string => (raw ?? '').replace(/[^0-9]/g, '');

export const isValidBusinessNumber = (raw: string | null | undefined): boolean => {
    const digits: string = businessNumberDigits(raw);
    if (digits.length !== 10) return false;
    const d: number[] = digits.split('').map(Number);
    let sum = 0;
    for (let i = 0; i < 9; i += 1) sum += d[i] * WEIGHTS[i];
    sum += Math.floor((d[8] * 5) / 10);
    return (10 - (sum % 10)) % 10 === d[9];
};

/** 000-00-00000 */
export const formatBusinessNumber = (raw: string | null | undefined): string => {
    const d: string = businessNumberDigits(raw);
    if (d.length <= 3) return d;
    if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5, 10)}`;
};

export const isValidHomepage = (raw: string | null | undefined): boolean => {
    const trimmed: string = (raw ?? '').trim();
    if (!trimmed || /\s/.test(trimmed)) return false;
    const withScheme: string = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`;
    try {
        const url = new URL(withScheme);
        return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname.includes('.') && !url.username && !url.password;
    } catch {
        return false;
    }
};

export const BUSINESS_NUMBER_ERROR = '사업자등록번호가 올바르지 않아요(숫자 10자리, 번호를 다시 확인해 주세요)';
export const HOMEPAGE_ERROR = '치과 홈페이지 주소가 올바르지 않아요(예: www.치과.com)';

/* ── 인증 전 치과 — 홈페이지 주소 또는 사업자등록증 사진(2026-10-10) ── */

export const BUSINESS_CERT_MAX_BYTES: number = 10 * 1024 * 1024;
/** 서버 형식 검사와 같은 목록(heic·webp 는 고르는 단계에서 거른다) */
export const BUSINESS_CERT_MIME: string[] = ['image/jpeg', 'image/png', 'application/pdf'];
export const PROOF_REQUIRED_ERROR = '사업자등록증 사진이나 홈페이지 주소 중 하나를 올려 주세요';

export type HomepageCertIssue = 'homepageInvalid' | 'proof';

/**
 * 인증 전 치과의 홈페이지·사업자등록증 칸 문제 — 서버 ClinicReadiness(PROOF)와 같은 규칙.
 * 둘 중 하나가 있어야 하고(둘 다 있어도 됨), 홈페이지는 적었으면 형식을 본다. 인증된 치과는 둘 다 선택(부르는 쪽이 거른다).
 */
export const homepageCertIssues = (state: { url: string | null | undefined; hasCert: boolean }): HomepageCertIssue[] => {
    const issues: HomepageCertIssue[] = [];
    const url: string = (state.url ?? '').trim();
    if (url && !isValidHomepage(url)) issues.push('homepageInvalid');
    if (!url && !state.hasCert) issues.push('proof');
    return issues;
};

/** 고른 파일의 문제(형식·크기) — 없으면 null. 크기는 압축한 뒤 파일로 본다 */
export const businessCertFileProblem = (file: { type: string; size: number }, stage: 'type' | 'size'): string | null => {
    if (stage === 'type') return BUSINESS_CERT_MIME.includes(file.type) ? null : 'jpg·png·pdf 파일만 올릴 수 있어요.';
    return file.size > BUSINESS_CERT_MAX_BYTES ? '사진은 10MB 이하만 올릴 수 있어요.' : null;
};
